import React, { useEffect, useState, useCallback, useMemo } from 'react';
import request from '../utils/request';

const TIER_PRICES = { basic: 100, premium: 250, enterprise: 500 };

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700',
  past_due: 'bg-amber-100 text-amber-700',
  trialing: 'bg-blue-100 text-blue-700',
  canceled: 'bg-red-100 text-red-600',
  none: 'bg-zinc-100 text-zinc-500',
};

const TIER_BADGE = {
  enterprise: 'bg-purple-100 text-purple-700',
  premium: 'bg-indigo-100 text-indigo-700',
  basic: 'bg-sky-100 text-sky-700',
  none: 'bg-zinc-100 text-zinc-500',
};

const PAYMENT_BADGE = {
  unpaid: 'bg-zinc-100 text-zinc-500',
  pending: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  refunded: 'bg-red-100 text-red-600',
};

const FILTER_OPTIONS = ['all', 'active', 'past_due', 'trialing', 'canceled', 'none'];

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ────────────────────────── Detail Drawer ────────────────────────── */
function HostDetailDrawer({ host, onClose, onUpdated }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [status, setStatus] = useState(host.conciergeSubscriptionStatus || 'none');
  const [tier, setTier] = useState(host.conciergeSubscriptionTier || 'none');
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const { data } = await request.get(`/admin/hosts/${host._id}/service-requests`);
      setRequests(data.serviceRequests || []);
      setProperties(data.properties || []);
    } catch (_) {}
    setLoadingDetail(false);
  }, [host._id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await request.patch(`/admin/hosts/${host._id}`, {
        conciergeSubscriptionStatus: status,
        conciergeSubscriptionTier: tier,
      });
      onUpdated();
    } catch (_) {}
    setSaving(false);
  };

  const reqsByLink = useMemo(() => {
    const map = {};
    requests.forEach((r) => {
      if (!map[r.linkKey]) map[r.linkKey] = 0;
      map[r.linkKey]++;
    });
    return map;
  }, [requests]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'requests', label: `Requests (${requests.length})` },
    { id: 'properties', label: `Properties (${properties.length})` },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white dark:bg-zinc-900 shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                {host.firstName} {host.lastName}
              </h2>
              <p className="text-sm text-zinc-500">{host.email}</p>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 text-xl font-bold">✕</button>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === t.id
                    ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                    : 'text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Host info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Phone</p>
                  <p className="text-sm text-zinc-800 dark:text-zinc-200 mt-1">{host.phoneNumber || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Company</p>
                  <p className="text-sm text-zinc-800 dark:text-zinc-200 mt-1">{host.companyName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Created</p>
                  <p className="text-sm text-zinc-800 dark:text-zinc-200 mt-1">{fmtDate(host.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Stripe Sub ID</p>
                  <p className="text-sm text-zinc-800 dark:text-zinc-200 mt-1 font-mono text-xs">
                    {host.stripeHostSubscriptionID || '—'}
                  </p>
                </div>
              </div>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Subscription controls */}
              <div>
                <h3 className="text-sm font-bold text-zinc-700 dark:text-zinc-300 mb-3">Subscription</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="past_due">Past Due</option>
                      <option value="trialing">Trialing</option>
                      <option value="canceled">Canceled</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">Tier</label>
                    <select
                      value={tier}
                      onChange={(e) => setTier(e.target.value)}
                      className="w-full border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                    >
                      <option value="enterprise">Enterprise ($500/mo)</option>
                      <option value="premium">Premium ($250/mo)</option>
                      <option value="basic">Basic ($100/mo)</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="mt-4 w-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 font-semibold text-sm py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-50 dark:bg-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{host.propertyCount}</p>
                  <p className="text-xs text-zinc-500 mt-1">Properties</p>
                </div>
                <div className="bg-zinc-50 dark:bg-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{host.requestCount}</p>
                  <p className="text-xs text-zinc-500 mt-1">Service Requests</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div>
              {loadingDetail ? (
                <p className="text-center text-zinc-400 py-8">Loading...</p>
              ) : requests.length === 0 ? (
                <p className="text-center text-zinc-400 py-8">No service requests</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 dark:border-zinc-700">
                        <th className="text-left py-2 px-2 text-xs font-semibold text-zinc-400 uppercase">Guest</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-zinc-400 uppercase">Service</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-zinc-400 uppercase">Date</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-zinc-400 uppercase">Status</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-zinc-400 uppercase">Payment</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold text-zinc-400 uppercase">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((r) => (
                        <tr key={r._id} className="border-b border-zinc-100 dark:border-zinc-800">
                          <td className="py-2 px-2 text-zinc-700 dark:text-zinc-300">{r.email}</td>
                          <td className="py-2 px-2 text-zinc-700 dark:text-zinc-300">{r.servicesRequested?.[0]}</td>
                          <td className="py-2 px-2 text-zinc-500">{fmtDate(r.serviceRequestDate)}</td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[r.status] || STATUS_BADGE.none}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PAYMENT_BADGE[r.paymentStatus] || PAYMENT_BADGE.unpaid}`}>
                              {r.paymentStatus}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-zinc-700 dark:text-zinc-300">
                            {r.price != null ? `$${r.price.toFixed(2)}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'properties' && (
            <div>
              {loadingDetail ? (
                <p className="text-center text-zinc-400 py-8">Loading...</p>
              ) : properties.length === 0 ? (
                <p className="text-center text-zinc-400 py-8">No properties</p>
              ) : (
                <div className="space-y-3">
                  {properties.map((p) => (
                    <div key={p._id} className="border border-zinc-200 dark:border-zinc-700 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                            {p.address?.line1 || '—'}, {p.address?.city || ''}
                          </p>
                          <p className="text-xs text-zinc-400 mt-0.5 font-mono">{p.uniqueId}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-zinc-900 dark:text-white">{reqsByLink[p.uniqueId] || 0}</p>
                          <p className="text-xs text-zinc-400">requests</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── Main Screen ────────────────────────── */
export default function SubscriptionDashboardScreen() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [drawerHost, setDrawerHost] = useState(null);
  const [bulkTier, setBulkTier] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchHosts = useCallback(async () => {
    try {
      const { data } = await request.get('/admin/hosts/subscriptions');
      setHosts(data.hosts || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchHosts(); }, [fetchHosts]);

  // Stats
  const stats = useMemo(() => {
    const active = hosts.filter((h) => h.conciergeSubscriptionStatus === 'active').length;
    const pastDue = hosts.filter((h) => h.conciergeSubscriptionStatus === 'past_due').length;
    const trialing = hosts.filter((h) => h.conciergeSubscriptionStatus === 'trialing').length;
    const mrr = hosts.reduce((sum, h) => {
      if (h.conciergeSubscriptionStatus === 'active') {
        return sum + (TIER_PRICES[h.conciergeSubscriptionTier] || 0);
      }
      return sum;
    }, 0);
    return { active, pastDue, trialing, mrr };
  }, [hosts]);

  // Filter + search + sort
  const displayed = useMemo(() => {
    let list = hosts;
    if (filter !== 'all') {
      list = list.filter((h) => h.conciergeSubscriptionStatus === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((h) =>
        `${h.firstName} ${h.lastName}`.toLowerCase().includes(q) ||
        h.email?.toLowerCase().includes(q) ||
        h.companyName?.toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (typeof av === 'number') return (av - bv) * dir;
      if (sortField === 'createdAt') return (new Date(av) - new Date(bv)) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [hosts, filter, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (field) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const allSelected = displayed.length > 0 && displayed.every((h) => selected.has(h._id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(displayed.map((h) => h._id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkApply = async () => {
    if (selected.size === 0) return;
    const updates = {};
    if (bulkTier) updates.conciergeSubscriptionTier = bulkTier;
    if (bulkStatus) updates.conciergeSubscriptionStatus = bulkStatus;
    if (Object.keys(updates).length === 0) return;

    setBulkSaving(true);
    try {
      await request.patch('/admin/hosts/bulk-update', {
        hostIds: Array.from(selected),
        updates,
      });
      await fetchHosts();
      setSelected(new Set());
      setBulkTier('');
      setBulkStatus('');
    } catch (_) {}
    setBulkSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900 dark:border-white" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-6">Subscription Management</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Active Subscribers', value: stats.active, color: 'text-emerald-600' },
          { label: 'Past Due', value: stats.pastDue, color: 'text-amber-600' },
          { label: 'Trialing', value: stats.trialing, color: 'text-blue-600' },
          { label: 'Monthly Revenue', value: `$${stats.mrr.toLocaleString()}`, color: 'text-zinc-900 dark:text-white' },
        ].map((s) => (
          <div key={s.label} className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{s.label}</p>
            <p className={`text-3xl font-bold mt-2 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-1">
          {FILTER_OPTIONS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize transition-colors ${
                filter === f
                  ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {f === 'past_due' ? 'Past Due' : f}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search hosts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-2 text-sm w-64 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
        />
        <span className="text-sm text-zinc-400 ml-auto">{displayed.length} host{displayed.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl px-4 py-3">
          <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
            {selected.size} selected
          </span>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white">
            <option value="">Set Status...</option>
            <option value="active">Active</option>
            <option value="past_due">Past Due</option>
            <option value="trialing">Trialing</option>
            <option value="canceled">Canceled</option>
            <option value="none">None</option>
          </select>
          <select value={bulkTier} onChange={(e) => setBulkTier(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white">
            <option value="">Set Tier...</option>
            <option value="enterprise">Enterprise</option>
            <option value="premium">Premium</option>
            <option value="basic">Basic</option>
            <option value="none">None</option>
          </select>
          <button
            onClick={handleBulkApply}
            disabled={bulkSaving || (!bulkTier && !bulkStatus)}
            className="bg-indigo-600 text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-opacity"
          >
            {bulkSaving ? 'Applying...' : 'Apply'}
          </button>
          <button
            onClick={() => { setSelected(new Set()); setBulkTier(''); setBulkStatus(''); }}
            className="text-sm text-zinc-500 hover:text-zinc-700 ml-auto"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
                <th className="w-10 px-4 py-3">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('firstName')}>
                  Host{sortIcon('firstName')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('conciergeSubscriptionTier')}>
                  Tier{sortIcon('conciergeSubscriptionTier')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('conciergeSubscriptionStatus')}>
                  Status{sortIcon('conciergeSubscriptionStatus')}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('propertyCount')}>
                  Properties{sortIcon('propertyCount')}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('requestCount')}>
                  Requests{sortIcon('requestCount')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Sub ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('createdAt')}>
                  Created{sortIcon('createdAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((h) => {
                const statusCls = STATUS_BADGE[h.conciergeSubscriptionStatus] || STATUS_BADGE.none;
                const tierCls = TIER_BADGE[h.conciergeSubscriptionTier] || TIER_BADGE.none;
                const subId = h.stripeHostSubscriptionID;
                return (
                  <tr
                    key={h._id}
                    className="border-b border-zinc-100 dark:border-zinc-700/50 hover:bg-zinc-50 dark:hover:bg-zinc-700/30 cursor-pointer transition-colors"
                    onClick={() => setDrawerHost(h)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(h._id)} onChange={() => toggleOne(h._id)} className="rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-zinc-800 dark:text-zinc-200">{h.firstName} {h.lastName}</p>
                      <p className="text-xs text-zinc-400">{h.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${tierCls}`}>
                        {h.conciergeSubscriptionTier || 'none'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusCls}`}>
                        {(h.conciergeSubscriptionStatus || 'none').replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-zinc-600 dark:text-zinc-400">{h.propertyCount}</td>
                    <td className="px-4 py-3 text-center text-zinc-600 dark:text-zinc-400">{h.requestCount}</td>
                    <td className="px-4 py-3 text-zinc-500 font-mono text-xs">
                      {subId && subId !== 'test' ? subId.slice(0, 14) + '...' : subId || '—'}
                    </td>
                    <td className="px-4 py-3 text-zinc-500">{fmtDate(h.createdAt)}</td>
                  </tr>
                );
              })}
              {displayed.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-zinc-400">
                    No hosts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Drawer */}
      {drawerHost && (
        <HostDetailDrawer
          host={drawerHost}
          onClose={() => setDrawerHost(null)}
          onUpdated={() => { fetchHosts(); setDrawerHost(null); }}
        />
      )}
    </div>
  );
}
